import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Users,
  Phone,
  Mail,
  Building,
  Eye,
  Edit,
  Search,
  Package,
  Plus,
  X,
  ChevronDown,
  Filter,
  Download,
  IndianRupee,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Upload
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  handleSearchInputChange,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import customerService from '../../../api/crm/customers/customerService';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import ExportModal from '../../../components/common/ExportModal';
import { generateExportFilename, downloadFromBackend, exportToCSV } from '../../../utils/exportUtils';
import { toast } from '../../../utils/toast';
// import { indianStates } from '../../../utils/indianStates';
import { formatDate } from '../../../utils/stringUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import BulkCustomerUpload from './BulkCustomerUpload';

const Customer360List = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // const [filterStatus, setFilterStatus] = useState('all');
  const [ordersRange, setOrdersRange] = useState({ min: '', max: '' });
  const [valueRange, setValueRange] = useState({ min: '', max: '' });

  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const [debouncedFilters, setDebouncedFilters] = useState({
    minOrders: '', maxOrders: '', minValue: '', maxValue: ''
  });
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, page: 1 }));
      setSelectedRows(new Set()); // Reset selection on search
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Debounce numeric filters
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters({
        minOrders: ordersRange.min,
        maxOrders: ordersRange.max,
        minValue: valueRange.min,
        maxValue: valueRange.max
      });
      // Only reset page if filters actually changed (prevents initial double fetch)
      // For simplicity, we just reset page on any text change in inputs
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);

    return () => clearTimeout(handler);
  }, [ordersRange, valueRange]);

  // Fetch Customer 360 KPIs using React Query
  const {
    data: kpisData,
    isLoading: kpiLoading
  } = useQuery({
    queryKey: ['customer360Kpis'],
    queryFn: async () => {
      const result = await customerService.getCustomer360Kpis();
      if (result.success) {
        return result.kpis || {
          totalCustomers: 0,
          totalOrders: 0,
          totalValue: 0
        };
      }
      throw new Error(result.error || 'Failed to load KPIs');
    }
  });

  const kpis = kpisData || {
    totalCustomers: 0,
    totalOrders: 0,
    totalValue: 0
  };

  // Fetch Customer 360 list data using React Query
  const {
    data: customersData,
    isLoading: loading,
    isError: isError,
    error: queryError
  } = useQuery({
    queryKey: ['customer360List', debouncedSearchTerm, /* filterStatus, */ pagination.page, itemsPerPage, debouncedFilters, dateRange],
    queryFn: async () => {
      const result = await customerService.getCustomer360List({
        search: debouncedSearchTerm.trim() || undefined,
        // status: filterStatus !== 'all' ? filterStatus : undefined,
        minOrders: debouncedFilters.minOrders !== '' ? debouncedFilters.minOrders : undefined,
        maxOrders: debouncedFilters.maxOrders !== '' ? debouncedFilters.maxOrders : undefined,
        minValue: debouncedFilters.minValue !== '' ? debouncedFilters.minValue : undefined,
        maxValue: debouncedFilters.maxValue !== '' ? debouncedFilters.maxValue : undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: pagination.page,
        limit: itemsPerPage
      });

      if (result.success) {
        // Update pagination state with response data
        setPagination(prev => ({
          ...prev,
          total: result.pagination?.total || 0,
          limit: result.pagination?.limit || itemsPerPage
        }));
        return {
          customers: result.customers || [],
          pagination: result.pagination || { page: 1, limit: itemsPerPage, total: 0 }
        };
      }
      throw new Error(result.error || 'Failed to load Customer 360 data');
    }
  });

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted customers for current page
  const getSortedCustomers = (data) => {
    if (!sortColumn || !data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'customerId') {
        aValue = (a.customerId || '').toLowerCase();
        bValue = (b.customerId || '').toLowerCase();
      } else if (sortColumn === 'name') {
        aValue = (a.companyName || a.name || '').toLowerCase();
        bValue = (b.companyName || b.name || '').toLowerCase();
      } else if (sortColumn === 'contact') {
        aValue = (a.contactPerson || '').toLowerCase();
        bValue = (b.contactPerson || '').toLowerCase();
      } else if (sortColumn === 'orders') {
        aValue = a.totalOrders || 0;
        bValue = b.totalOrders || 0;
      } else if (sortColumn === 'value') {
        aValue = a.totalValue || 0;
        bValue = b.totalValue || 0;
      } else if (sortColumn === 'update') {
        aValue = new Date(a.updatedAt || a.syncedAt || a.createdAt || 0).getTime();
        bValue = new Date(b.updatedAt || b.syncedAt || b.createdAt || 0).getTime();
      } else if (sortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      const order = sortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  const customers = customersData?.customers || [];
  const error = queryError?.message || null;


  // Capitalize first letter of each word
  const capitalizeText = (text) => {
    if (!text) return '';
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  const handleViewCustomer = (customerId) => {
    // Clear search and reset pagination when navigating to detail
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    navigate(`/customer_360/${customerId}`);
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPagination(prev => ({ ...prev, page: 1, limit }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
    setSelectedRows(new Set()); // Reset selection on page change
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = customers.map(c => c._id || c.id || c.customerId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const mapCustomersForExport = (customersToExport) => {
    return customersToExport.map(customer => {
      const city = customer.billingAddress?.city || customer.shippingAddress?.city || '';
      const state = customer.billingAddress?.state || customer.shippingAddress?.state || '';
      return {
        'Customer ID': customer.customerId || 'N/A',
        'Company Name': customer.companyName || '',
        'Contact Person': customer.contactPerson || '',
        'Email': customer.email || '',
        'Phone': customer.phone || '',
        'City': city,
        'State': state,
        'Status': customer.isActive === false ? 'Blocked' : 'Active',
        'Total Orders': customer.totalOrders || 0,
        'Total Value': (customer.totalValue || 0).toFixed(2),
        'Created Date': customer.createdAt ? formatDate(customer.createdAt, { includeTime: false }) : '',
        'Update Date': (customer.updatedAt || customer.syncedAt || customer.createdAt) ? formatDate((customer.updatedAt || customer.syncedAt || customer.createdAt), { includeTime: false }) : ''
      };
    });
  };

  const handleExportCurrentPage = () => {
    const exportData = mapCustomersForExport(customers);
    exportToCSV(exportData, 'customer_360_page', {
      title: 'Customer List',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      const customersToExport = customers.filter(c => selectedRows.has(c._id || c.id || c.customerId));
      const exportData = mapCustomersForExport(customersToExport);
      exportToCSV(exportData, 'customer_360_selected', {
        title: 'Customer List',
        type: 'Selected Customers'
      });
      setSelectedRows(new Set()); // Optional: clear selection after export
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const filters = {
        search: debouncedSearchTerm.trim(),
        // status: filterStatus !== 'all' ? filterStatus : undefined,
        minOrders: debouncedFilters.minOrders !== '' ? debouncedFilters.minOrders : undefined,
        maxOrders: debouncedFilters.maxOrders !== '' ? debouncedFilters.maxOrders : undefined,
        minValue: debouncedFilters.minValue !== '' ? debouncedFilters.minValue : undefined,
        maxValue: debouncedFilters.maxValue !== '' ? debouncedFilters.maxValue : undefined,
        startDate,
        endDate
      };

      const result = await customerService.exportCustomer360List(filters);

      if (result.success && result.downloadUrl) {
        const filename = generateExportFilename('customer_360_range');
        await downloadFromBackend(result.downloadUrl, filename);
        toast.success('Export started successfully');
        setShowExportModal(false);
      } else {
        throw new Error(result.error || 'Failed to get export URL');
      }
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const filters = {
        search: debouncedSearchTerm.trim(),
        // status: filterStatus !== 'all' ? filterStatus : undefined,
        minOrders: debouncedFilters.minOrders !== '' ? debouncedFilters.minOrders : undefined,
        maxOrders: debouncedFilters.maxOrders !== '' ? debouncedFilters.maxOrders : undefined,
        minValue: debouncedFilters.minValue !== '' ? debouncedFilters.minValue : undefined,
        maxValue: debouncedFilters.maxValue !== '' ? debouncedFilters.maxValue : undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined
      };

      const result = await customerService.exportCustomer360List(filters);

      if (result.success && result.downloadUrl) {
        const filename = generateExportFilename('customer_360_all');
        await downloadFromBackend(result.downloadUrl, filename);
        toast.success('All customer export started');
        setShowExportModal(false);
      } else {
        throw new Error(result.error || 'Failed to get export URL');
      }
    } catch (error) {
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center gap-4">
            <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Customer 360°</h1>
              <p className="text-sm sm:text-base text-slate-500 mt-1">View and manage all customer profiles</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <PermissionGate permission="customer_management" action="read">
              <button
                onClick={handleExportClick}
                disabled={!customers || customers.length === 0}
                className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
              </button>
            </PermissionGate>
            <PermissionGate permission="customer_management" action="create">
              <button
                onClick={() => setShowBulkUpload(true)}
                className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              >
                <Upload className="h-4 w-4" />
                <span>Bulk Upload</span>
              </button>
            </PermissionGate>
            <PermissionGate permission="customer_management" action="create">
              <button
                onClick={() => navigate('/customer_360/add')}
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              >
                <Plus className="h-4 w-4" />
                <span>Add Customer</span>
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {kpiLoading ? '...' : kpis.totalCustomers}
              </p>
            </div>
            <User className="h-8 w-8 text-brand" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-purple-600">
                {kpiLoading ? '...' : kpis.totalOrders}
              </p>
            </div>
            <Package className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-green-600">
                {kpiLoading ? '...' : `₹${formatIndianAmount(kpis.totalValue || 0)}`}
              </p>
            </div>
            <IndianRupee className="h-8 w-8 text-green-600 font-bold text-2xl" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">

          {/* Search Bar */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search customers id, name, company, email, phone..."
              value={searchTerm}
              onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
              maxLength={FIELD_LIMITS.search}
              className="input-field pl-10 pr-4 py-2 w-full"
              autoComplete="off"
            />
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto" ref={dropdownRef}>

            {/* Orders Dropdown */}
            <div className="relative">
              <button
                onClick={() => setActiveDropdown(activeDropdown === 'orders' ? null : 'orders')}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${activeDropdown === 'orders' || ordersRange.min || ordersRange.max
                  ? 'border-brand text-brand bg-blue-50'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <span>Order Count</span>
                {(ordersRange.min || ordersRange.max) && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white text-[10px] filter-badge">
                    {(ordersRange.min && ordersRange.max) ? 2 : 1}
                  </span>
                )}
                <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'orders' ? 'rotate-180' : ''}`} />
              </button>

              {activeDropdown === 'orders' && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 p-4 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900">Filter by Orders</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Min Orders</label>
                        <input
                          type="number"
                          placeholder="0"
                          value={ordersRange.min}
                          onKeyDown={(e) => {
                            if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || (/^\d+$/.test(val) && parseInt(val, 10) >= 0)) {
                              setOrdersRange(prev => ({ ...prev, min: val }));
                            }
                          }}
                          className="input-field w-full px-3 py-2 text-sm"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Max Orders</label>
                        <input
                          type="number"
                          placeholder="∞"
                          value={ordersRange.max}
                          onKeyDown={(e) => {
                            if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || (/^\d+$/.test(val) && parseInt(val, 10) >= 0)) {
                              setOrdersRange(prev => ({ ...prev, max: val }));
                            }
                          }}
                          className="input-field w-full px-3 py-2 text-sm"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Value Dropdown */}
            <div className="relative">
              <button
                onClick={() => setActiveDropdown(activeDropdown === 'value' ? null : 'value')}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${activeDropdown === 'value' || valueRange.min || valueRange.max
                  ? 'border-brand text-brand bg-blue-50'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <span>Total Value</span>
                {(valueRange.min || valueRange.max) && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white text-[10px]">
                    {(valueRange.min && valueRange.max) ? 2 : 1}
                  </span>
                )}
                <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'value' ? 'rotate-180' : ''}`} />
              </button>

              {activeDropdown === 'value' && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 p-4 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900">Filter by Value (₹)</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Min Value</label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                          <input
                            type="number"
                            placeholder="0"
                            value={valueRange.min}
                            onKeyDown={(e) => {
                              if (['-', '+', 'e', 'E'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || (/^\d*\.?\d{0,2}$/.test(val) && parseFloat(val) >= 0)) {
                                setValueRange(prev => ({ ...prev, min: val }));
                              }
                            }}
                            className="input-field w-full pl-6 pr-3 py-2 text-sm"
                            min="0"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Max Value</label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                          <input
                            type="number"
                            placeholder="∞"
                            value={valueRange.max}
                            onKeyDown={(e) => {
                              if (['-', '+', 'e', 'E'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || (/^\d*\.?\d{0,2}$/.test(val) && parseFloat(val) >= 0)) {
                                setValueRange(prev => ({ ...prev, max: val }));
                              }
                            }}
                            className="input-field w-full pl-6 pr-3 py-2 text-sm"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              allowFutureDates={false}
            />

            {/* Clear Button */}
            {(searchTerm || ordersRange.min || ordersRange.max || valueRange.min || valueRange.max || dateRange.startDate || dateRange.endDate) && (
              <button
                className="btn-cancel flex items-center space-x-2 h-10 px-4"
                onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearchTerm('');
                  setOrdersRange({ min: '', max: '' });
                  setValueRange({ min: '', max: '' });
                  setDateRange({ startDate: '', endDate: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                  setActiveDropdown(null);
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </button>
            )}
          </div>

        </div>


      </div>


      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                    checked={customers.length > 0 && selectedRows.size === customers.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('customerId')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Customer ID</span>
                    {sortColumn === 'customerId' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Customer</span>
                    {sortColumn === 'name' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('contact')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Contact</span>
                    {sortColumn === 'contact' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('orders')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Orders</span>
                    {sortColumn === 'orders' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('value')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Value</span>
                    {sortColumn === 'value' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created At</span>
                    {sortColumn === 'createdAt' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('update')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Update</span>
                    {sortColumn === 'update' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mb-2"></div>
                      <p>Loading customers...</p>
                    </div>
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-red-500">
                    {error || 'Failed to load Customer 360 data'}
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    No customers found matching your search criteria.
                  </td>
                </tr>
              ) : (
                getSortedCustomers(customers).map((customer) => {
                  const displayName = customer.companyName || customer.contactPerson || 'N/A';
                  const contactName = customer.contactPerson || '';
                  const phoneFull = customer.phone || '';
                  const emailFull = customer.email || '';

                  return (
                    <tr key={customer._id || customer.id} className="hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                          checked={selectedRows.has(customer._id || customer.id || customer.customerId)}
                          onChange={() => handleSelectRow(customer._id || customer.id || customer.customerId)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.customerId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 relative group">
                            {customer.profileImage ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover border border-gray-200 shadow-sm transition-transform group-hover:scale-110"
                                src={customer.profileImage}
                                alt=""
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-xs bg-gradient-to-br from-green-500 to-blue-600 shadow-sm ${customer.profileImage ? 'hidden' : 'flex'}`}
                              style={{ display: customer.profileImage ? 'none' : 'flex' }}
                            >
                              {(contactName || displayName || 'N/A')
                                .split(' ')
                                .filter(Boolean)
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </div>
                          </div>
                          <div className="ml-2">
                            <div
                              className="text-sm font-semibold text-gray-900 max-w-[200px] truncate capitalize cursor-pointer hover:text-brand transition-colors"
                              title={contactName || displayName}
                              onClick={() => handleViewCustomer(customer._id || customer.id)}
                            >
                              {capitalizeText(contactName || displayName)}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1 max-w-[200px]">
                              <Building className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate capitalize" title={displayName}>
                                {capitalizeText(displayName)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center gap-1 max-w-xs">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate" title={phoneFull[0]}>
                            {phoneFull[0]}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1 max-w-xs">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate" title={emailFull}>
                            {emailFull}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.totalOrders || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ₹{formatIndianAmount(customer.totalValue || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.createdAt ? formatDate(customer.createdAt, { includeTime: true }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {(customer.updatedAt || customer.syncedAt || customer.createdAt) ? formatDate(customer.updatedAt || customer.syncedAt || customer.createdAt, { includeTime: true }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <PermissionGate permission="customer_management" action="read">
                            <button
                              onClick={() => handleViewCustomer(customer._id || customer.id)}
                              className="text-brand hover:text-[#012698]"
                              title="View Customer"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="customer_management" action="update">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer_360/edit/${customer._id || customer.id}`);
                              }}
                              className="text-brand hover:text-[#012698]"
                              title="Edit Customer"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(() => {
          const responsePagination = customersData?.pagination || pagination;
          const totalItems = Number(responsePagination?.total || 0);
          const totalPages = Math.ceil(totalItems / (responsePagination?.limit || itemsPerPage || 10)) || 1;
          const currentPage = responsePagination?.page || pagination.page || 1;
          const shouldShowPagination = !loading && totalPages > 1;

          if (!shouldShowPagination) return null;

          return (
            <div className="mt-6">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                limit={itemsPerPage}
                hasNextPage={currentPage < totalPages}
                hasPrevPage={currentPage > 1}
                onPageChange={handlePageChange}
                onLimitChange={handleItemsPerPageChange}
                limitOptions={[10, 25, 50, 100]}
              />
            </div>
          );
        })()}
      </div>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        loading={exportLoading}
        title="Export Customer List"
        allDataLabel="Export All Filtered Customers"
      />
      {/* Bulk Upload Modal */}
      <BulkCustomerUpload
        isOpen={showBulkUpload}
        onCancel={() => setShowBulkUpload(false)}
        onComplete={() => {
          queryClient.invalidateQueries(['customer360List']);
          queryClient.invalidateQueries(['customer360Kpis']);
        }}
      />
    </div>
  );
};

export default Customer360List;
